import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FaFileExcel } from 'react-icons/fa';
import { FormField, Select } from '../../../components/EntityList';
import { toast } from '../../../toasts';
import { useLabels } from '../../../hooks/useLabels';
import { readWorkbook, SUPPORTED_EXTENSIONS } from './importFile';

const FILE_ERRORS = {
	UNSUPPORTED_FORMAT: `Formato no soportado. Usá ${SUPPORTED_EXTENSIONS.map((e) => `.${e}`).join(', ')}.`,
	FILE_READ_ERROR: 'No se pudo leer el archivo.',
	FILE_PARSE_ERROR: 'El archivo no es un Excel/CSV válido.',
};

export default function StepFile({
	entities,
	entityKey,
	onEntityChange,
	file,
	onFile,
	sheetName,
	onSheetChange,
	table,
	maxRows,
}) {
	const { t } = useLabels();
	const inputRef = useRef(null);
	const [dragging, setDragging] = useState(false);
	const [loading, setLoading] = useState(false);

	const loadFile = async (selected) => {
		if (!selected) return;
		setLoading(true);
		try {
			const workbook = await readWorkbook(selected);
			onFile({ name: selected.name, workbook });
		} catch (error) {
			toast.error(FILE_ERRORS[error.message] || FILE_ERRORS.FILE_PARSE_ERROR);
		} finally {
			setLoading(false);
		}
	};

	const rowCount = table?.rows.length || 0;

	return (
		<div className='tw:space-y-5'>
			{entities.length > 1 && (
				<FormField id='import-entity' label={t('import.entityQuestion')}>
					<Select
						id='import-entity'
						value={entityKey}
						onChange={(e) => onEntityChange(e.target.value)}
						options={entities.map((entity) => ({ value: entity.key, label: t(entity.labelKey) }))}
					/>
				</FormField>
			)}

			<div
				role='button'
				tabIndex={0}
				onClick={() => inputRef.current?.click()}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
				}}
				onDragOver={(e) => {
					e.preventDefault();
					setDragging(true);
				}}
				onDragLeave={() => setDragging(false)}
				onDrop={(e) => {
					e.preventDefault();
					setDragging(false);
					loadFile(e.dataTransfer.files?.[0]);
				}}
				className={classnames(
					'tw:flex tw:flex-col tw:items-center tw:gap-2 tw:rounded-panel tw:border-2 tw:border-dashed tw:px-6 tw:py-8 tw:text-center tw:cursor-pointer tw:transition-colors',
					dragging ? 'tw:border-accent tw:bg-accent-soft' : 'tw:border-border-strong tw:bg-surface-2',
				)}
			>
				<FaFileExcel className='tw:text-[28px] tw:text-ok' />
				<span className='tw:text-[13.5px] tw:text-ink-0'>
					{loading ? 'Leyendo archivo...' : file ? <strong>{file.name}</strong> : t('import.fileHelp')}
				</span>
				{file && <span className='tw:text-[12px] tw:text-ink-3'>{t('import.fileHelp')}</span>}
				<input
					ref={inputRef}
					type='file'
					accept={SUPPORTED_EXTENSIONS.map((e) => `.${e}`).join(',')}
					className='tw:hidden'
					onChange={(e) => {
						loadFile(e.target.files?.[0]);
						e.target.value = '';
					}}
				/>
			</div>

			{file && (
				<>
					<FormField id='import-sheet' label={t('import.sheet')} className='tw:max-w-[320px]'>
						<Select
							id='import-sheet'
							value={sheetName}
							onChange={(e) => onSheetChange(e.target.value)}
							options={file.workbook.SheetNames}
						/>
					</FormField>

					{table && (
						<div className='tw:space-y-2'>
							<p className='tw:m-0 tw:text-[13px] tw:text-ink-2'>
								<strong>{rowCount}</strong> {t('import.rowsFound')} · {table.headers.length} columnas
							</p>
							<div className='tw:flex tw:flex-wrap tw:gap-1.5'>
								{table.headers.map((header) => (
									<span
										key={header}
										className='tw:rounded-full tw:bg-surface-3 tw:px-2.5 tw:py-0.5 tw:text-[12px] tw:text-ink-1'
									>
										{header}
									</span>
								))}
							</div>
							{rowCount > maxRows && (
								<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px]'>
									{t('import.tooManyRows').replace('{count}', rowCount).replace('{max}', maxRows)}
								</div>
							)}
						</div>
					)}
				</>
			)}
		</div>
	);
}

StepFile.propTypes = {
	entities: PropTypes.array.isRequired,
	entityKey: PropTypes.string.isRequired,
	onEntityChange: PropTypes.func.isRequired,
	file: PropTypes.object,
	onFile: PropTypes.func.isRequired,
	sheetName: PropTypes.string,
	onSheetChange: PropTypes.func.isRequired,
	table: PropTypes.object,
	maxRows: PropTypes.number.isRequired,
};
